import React, { useEffect } from 'react';
import { parseDictEntry, parseLithologyName } from '../firebase/dictionaries';

const MIN_THICKNESS = 0.1;
const LAYERING_OPTIONS = ['+', '/', '//'];

// Tryb Skala uzywa formatu "SM(GH)" - skala (grunt w nawiasie).
// Parser i serializer dla composite shortForm.
function parseRockComposite(short) {
  if (!short) return { rockShort: '', soilShort: '' };
  const rock = short.match(/^([^(]+)/);
  const soil = short.match(/\(([^)]+)\)/);
  return {
    rockShort: rock ? rock[1].trim() : '',
    soilShort: soil ? soil[1].trim() : '',
  };
}
function parseRockCompositeFull(full) {
  if (!full) return { rockFull: '', soilFull: '' };
  const m = full.match(/^([^(]+)(?:\s*\(([^)]+)\))?/);
  return {
    rockFull: (m?.[1] || '').trim(),
    soilFull: (m?.[2] || '').trim(),
  };
}
function buildRockComposite(rockShort, rockFull, soilShort, soilFull) {
  if (!rockShort) return { shortForm: '', fullName: '' };
  return {
    shortForm: soilShort ? `${rockShort}(${soilShort})` : rockShort,
    fullName: soilFull ? `${rockFull} (${soilFull})` : rockFull,
  };
}

// Parser dla composite wewnatrz nawiasow w trybie Skala: "Li // Pc" lub "Pd + Ps / Pg" itp.
// Zwraca do 3 elementow i 2 separatory. Akceptuje brak spacji wokol separatora.
function parseSubComposite(s) {
  const empty = { p1: '', sep1: '', p2: '', sep2: '', p3: '' };
  if (!s) return empty;
  const m = s.match(/^(.+?)(?:\s*(\+|\/\/|\/)\s*(.+?)(?:\s*(\+|\/\/|\/)\s*(.+))?)?$/);
  if (!m) return { ...empty, p1: s.trim() };
  return {
    p1: (m[1] || '').trim(),
    sep1: m[2] || '',
    p2: (m[3] || '').trim(),
    sep2: m[4] || '',
    p3: (m[5] || '').trim(),
  };
}
function buildSubComposite(p1, sep1, p2, sep2, p3) {
  if (!p1) return '';
  let out = p1;
  if (p2) out += (sep1 ? ` ${sep1} ` : ' ') + p2;
  if (p3) out += (sep2 ? ` ${sep2} ` : ' ') + p3;
  return out;
}

/**
 * Dialog edycji pelnej litologii warstwy.
 * Props:
 *  - dialog: stan z useBoreholes.editingLayerDialog
 *  - setDialog: setter
 *  - onSubmit: handler zapisu
 *  - onCancel: handler anulowania
 *  - lithoDict, genesisDict, rockDict, numberDict: dane slownikowe z useLithologyDictionaries
 *  - prevLayer, nextLayer: sasiednie warstwy (do walidacji Od/Do); null jesli brzeg
 *  - maxDepth: glebokosc otworu (gorny limit dla 'to' ostatniej warstwy)
 *  - nameInputRef: ref do focusa pierwszego pola
 *  - thicknessInputRef: nieuzywany w nowym layoucie, zachowany dla zgodnosci API
 */
export default function LayerEditDialog({
  dialog,
  setDialog,
  onSubmit,
  onCancel,
  lithoDict = [],
  genesisDict = [],
  rockDict = [],
  rockSubDict = [],
  numberDict = [],
  prevLayer = null,
  nextLayer = null,
  maxDepth = 100,
  nameInputRef,
}) {
  // Auto-wypełnij sloty litologii z `name` przy otwarciu dialogu (jeśli puste a name istnieje)
  useEffect(() => {
    if (!dialog) return;
    if (dialog.lithologyRockSoil01 || dialog.lithologyRockSoil02 || dialog.lithologyRockSoil03) return;
    if (!dialog.name) return;
    if (!lithoDict.length && !rockDict.length && !rockSubDict.length) return; // słowniki jeszcze nie załadowane
    const isRockMode = (dialog.materialType || 'grunt') === 'skala';
    const parsed = parseLithologyName(dialog.name, { lithoDict, rockDict, rockSubDict, isRock: isRockMode });
    if (!parsed.lithologyRockSoil01 && !parsed.lithologyRockSoil02) return;
    setDialog(prev => prev ? { ...prev, ...parsed } : prev);
  }, [dialog?.boreholeId, dialog?.layerId, lithoDict.length, rockDict.length, rockSubDict.length]);

  if (!dialog) return null;

  const update = (patch) => setDialog({ ...dialog, ...patch });

  const fromMin = prevLayer ? Number(prevLayer.to ?? prevLayer.from ?? 0) : 0;
  const fromMax = Math.max(fromMin + MIN_THICKNESS, Number(dialog.to) - MIN_THICKNESS);
  const toMin = Math.max(0, Number(dialog.from) + MIN_THICKNESS);
  const toMax = nextLayer ? Number(nextLayer.from ?? maxDepth) - MIN_THICKNESS : maxDepth;

  const clamp = (v, mn, mx) => Math.max(mn, Math.min(mx, v));

  const isRock = (dialog.materialType || 'grunt') === 'skala';

  // Wartosci skladowe dla trybu Skala (rozparsowane z lithologyRockSoil01)
  const setSlot = (slot, fullString) => {
    const { shortForm, fullName } = parseDictEntry(fullString);
    if (slot === 1) update({ lithologyRockSoil01: shortForm, lithologyRockSoilFullName01: fullName });
    if (slot === 2) update({ lithologyRockSoil02: shortForm, lithologyRockSoilFullName02: fullName });
    if (slot === 3) update({ lithologyRockSoil03: shortForm, lithologyRockSoilFullName03: fullName });
  };

  // Self-heal: jesli warstwa ma sam short bez full name, szukamy full po short w slownikach.
  // Kolejnosc: rockSubDict (podkat. skal) → rockDict (skaly glowne) → lithoDict (grunty)
  const lookupFullByShort = (short) => {
    if (!short) return '';
    const found = rockSubDict.find(e => e.shortForm === short)
      || rockDict.find(e => e.shortForm === short)
      || lithoDict.find(e => e.shortForm === short);
    return found?.fullName || '';
  };

  // Pomocniki dla trybu Skala — kazdy slot 01/02/03 to composite "skala(grunt-composite)"
  // gdzie grunt-composite to do 3 podkategorii + 2 sub-przewarstwienia (jak w trybie Grunt).
  const parseSlotFull = (slotIdx) => {
    const short = dialog[`lithologyRockSoil0${slotIdx}`] || '';
    const full = dialog[`lithologyRockSoilFullName0${slotIdx}`] || '';
    const { rockShort } = parseRockComposite(short);
    let { rockFull } = parseRockCompositeFull(full);
    if (rockShort && !rockFull) rockFull = lookupFullByShort(rockShort);
    const soilCompShort = short.match(/\(([^)]+)\)/)?.[1] || '';
    const soilCompFull = full.match(/\(([^)]+)\)/)?.[1] || '';
    const ss = parseSubComposite(soilCompShort);
    const sf = parseSubComposite(soilCompFull);
    const healSub = (s, f) => (s && !f) ? lookupFullByShort(s) : f;
    return {
      rockShort, rockFull,
      sub1: { short: ss.p1, full: healSub(ss.p1, sf.p1) },
      sep1: ss.sep1,
      sub2: { short: ss.p2, full: healSub(ss.p2, sf.p2) },
      sep2: ss.sep2,
      sub3: { short: ss.p3, full: healSub(ss.p3, sf.p3) },
    };
  };

  const rebuildSlot = (slotIdx, parts) => {
    const { rockShort, rockFull, sub1, sep1, sub2, sep2, sub3 } = parts;
    const soilCompShort = buildSubComposite(sub1.short, sep1, sub2.short, sep2, sub3.short);
    const soilCompFull = buildSubComposite(sub1.full, sep1, sub2.full, sep2, sub3.full);
    let composite = '';
    let compositeFull = '';
    if (rockShort) {
      composite = soilCompShort ? `${rockShort}(${soilCompShort})` : rockShort;
      compositeFull = soilCompFull ? `${rockFull} (${soilCompFull})` : rockFull;
    }
    const patch = {
      [`lithologyRockSoil0${slotIdx}`]: composite,
      [`lithologyRockSoilFullName0${slotIdx}`]: compositeFull,
    };
    if (slotIdx === 1) patch.lithologyRock01 = rockShort;
    update(patch);
  };

  const setRockPart = (slotIdx, fullString) => {
    const { shortForm, fullName } = parseDictEntry(fullString);
    const cur = parseSlotFull(slotIdx);
    rebuildSlot(slotIdx, { ...cur, rockShort: shortForm, rockFull: fullName });
  };
  const setSubSlot = (slotIdx, subIdx, fullString) => {
    const { shortForm, fullName } = parseDictEntry(fullString);
    const cur = parseSlotFull(slotIdx);
    const newCur = { ...cur };
    newCur[`sub${subIdx}`] = { short: shortForm, full: fullName };
    rebuildSlot(slotIdx, newCur);
  };
  const setSubSep = (slotIdx, sepIdx, sep) => {
    const cur = parseSlotFull(slotIdx);
    const newCur = { ...cur };
    newCur[`sep${sepIdx}`] = sep;
    rebuildSlot(slotIdx, newCur);
  };

  const switchMaterialType = (newType) => {
    // Zostawiamy sloty 02/03 + przewarstwienia bez zmian — to samo schema dziala dla Gruntu i Skaly
    update({ materialType: newType });
  };

  // Wartosci slotow gruntu jako fullString (do <select value>)
  // Wartość <select> musi być IDENTYCZNA ze stringiem opcji. Nie da się jej skleić
  // z pól warstwy, bo opis w danych i w słowniku różni się wielkością liter
  // ("Pd - piasek drobny" w warstwie vs "Pd - Piasek drobny" w słowniku).
  // Szukamy więc wpisu po samym skrócie i bierzemy jego fullString.
  const slotValue = (short, full, dict = []) => {
    if (!short) return '';
    const entry = dict.find(e => e.shortForm === short);
    if (entry) return entry.fullString;
    // Skrót spoza słownika (np. Gb, T, Pd(g)) — zostawiamy to co w danych,
    // brakującą opcję dorobi optionsWithCurrent().
    return full ? `${short} - ${full}` : short;
  };

  // Dokłada opcję dla wartości spoza słownika, żeby aktualne ustawienie było
  // widoczne i nie zniknęło przy zapisie.
  const optionsWithCurrent = (dict, currentValue) => {
    const opts = dict.map(e => ({ value: e.fullString, label: e.fullString }));
    if (currentValue && !dict.some(e => e.fullString === currentValue)) {
      opts.unshift({ value: currentValue, label: `${currentValue}  (spoza słownika)` });
    }
    return opts;
  };

  const slot1Value = slotValue(dialog.lithologyRockSoil01, dialog.lithologyRockSoilFullName01, lithoDict);
  const slot2Value = slotValue(dialog.lithologyRockSoil02, dialog.lithologyRockSoilFullName02, lithoDict);
  const slot3Value = slotValue(dialog.lithologyRockSoil03, dialog.lithologyRockSoilFullName03, lithoDict);
  // Geneza: w bazie trzymany jest sam skrót ("Qh"), a opcje mają "Qh - holocen".
  const genesisValue = slotValue(dialog.genesisAndStratigraphy, '', genesisDict);

  // Slownik podkategorii skal — dedykowana kolekcja Firestore `subCatRockySoil`.
  const subDict = rockSubDict;

  // Dla trybu Skala — rozparsowane elementy per slot (rock + 3 sub-sloty + 2 sub-sep)
  const rockSlots = [1, 2, 3].map(i => {
    const s = parseSlotFull(i);
    return {
      idx: i,
      rockValue: slotValue(s.rockShort, s.rockFull, rockDict),
      sub1Value: slotValue(s.sub1.short, s.sub1.full, rockSubDict),
      sub2Value: slotValue(s.sub2.short, s.sub2.full, rockSubDict),
      sub3Value: slotValue(s.sub3.short, s.sub3.full, rockSubDict),
      sep1: s.sep1,
      sep2: s.sep2,
    };
  });

  return (
    <>
      <div
        style={{
          position: 'fixed',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1000,
          background: 'white',
          padding: '20px',
          border: '2px solid #3B82F6',
          borderRadius: '12px',
          boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
          width: '880px',
          maxWidth: '95vw',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
        onKeyDown={(e) => { if (e.key === 'Escape') onCancel(); }}
      >
        <h3 className="text-lg font-bold text-gray-800 mb-4">Edycja warstwy</h3>

        {/* Od / Do */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Od (m):</label>
            <input
              ref={nameInputRef}
              type="number"
              step="0.1"
              value={dialog.from ?? 0}
              onChange={(e) => {
                const v = clamp(parseFloat(e.target.value) || 0, fromMin, fromMax);
                update({ from: v });
              }}
              className="border-2 border-gray-300 focus:border-blue-500 rounded px-3 py-2 w-full outline-none"
            />
            <div className="text-xs text-gray-400 mt-1">min: {fromMin.toFixed(2)}, max: {fromMax.toFixed(2)}</div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Do (m):</label>
            <input
              type="number"
              step="0.1"
              value={dialog.to ?? 0}
              onChange={(e) => {
                const v = clamp(parseFloat(e.target.value) || 0, toMin, toMax);
                update({ to: v });
              }}
              className="border-2 border-gray-300 focus:border-blue-500 rounded px-3 py-2 w-full outline-none"
            />
            <div className="text-xs text-gray-400 mt-1">min: {toMin.toFixed(2)}, max: {toMax.toFixed(2)}</div>
          </div>
        </div>

        {/* Grunt / Skala */}
        <div className="mb-4">
          <label className="block text-sm font-semibold text-gray-700 mb-2">Litologia:</label>
          <div className="flex gap-4 mb-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name="materialType" checked={!isRock} onChange={() => switchMaterialType('grunt')} />
              <span className="text-sm">Grunt</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name="materialType" checked={isRock} onChange={() => switchMaterialType('skala')} />
              <span className="text-sm">Skała</span>
            </label>
          </div>

          {!isRock && (
            <>
              {/* Sloty gruntu 1/2/3 + przewarstwienia */}
              <div className="space-y-2">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Litologia Gruntu 1:</label>
                  <select
                    value={slot1Value}
                    onChange={(e) => setSlot(1, e.target.value)}
                    className="border border-gray-300 focus:border-blue-500 rounded px-2 py-1.5 w-full text-sm outline-none"
                    disabled={lithoDict.length === 0}
                  >
                    <option value="">-- wybierz --</option>
                    {optionsWithCurrent(lithoDict, slot1Value).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-[80px_1fr] gap-2 items-end">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Przew. 1:</label>
                    <select
                      value={dialog.layeringRockSoil01 || ''}
                      onChange={(e) => update({ layeringRockSoil01: e.target.value })}
                      className="border border-gray-300 rounded px-2 py-1.5 w-full text-sm outline-none"
                    >
                      <option value="">--</option>
                      {LAYERING_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Litologia Gruntu 2:</label>
                    <select
                      value={slot2Value}
                      onChange={(e) => setSlot(2, e.target.value)}
                      className="border border-gray-300 rounded px-2 py-1.5 w-full text-sm outline-none"
                      disabled={lithoDict.length === 0}
                    >
                      <option value="">-- wybierz --</option>
                      {optionsWithCurrent(lithoDict, slot2Value).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-[80px_1fr] gap-2 items-end">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Przew. 2:</label>
                    <select
                      value={dialog.layeringRockSoil02 || ''}
                      onChange={(e) => update({ layeringRockSoil02: e.target.value })}
                      className="border border-gray-300 rounded px-2 py-1.5 w-full text-sm outline-none"
                    >
                      <option value="">--</option>
                      {LAYERING_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Litologia Gruntu 3:</label>
                    <select
                      value={slot3Value}
                      onChange={(e) => setSlot(3, e.target.value)}
                      className="border border-gray-300 rounded px-2 py-1.5 w-full text-sm outline-none"
                      disabled={lithoDict.length === 0}
                    >
                      <option value="">-- wybierz --</option>
                      {optionsWithCurrent(lithoDict, slot3Value).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            </>
          )}

          {isRock && (
            <div className="space-y-3">
              {rockSlots.map((s, sIdx) => (
                <React.Fragment key={s.idx}>
                  {sIdx > 0 && (
                    <div className="pl-2">
                      <label className="block text-xs text-gray-500 mb-1">Przewarstwienie {sIdx} (między slotami):</label>
                      <select
                        value={dialog[`layeringRockSoil0${sIdx}`] || ''}
                        onChange={(e) => update({ [`layeringRockSoil0${sIdx}`]: e.target.value })}
                        className="border border-gray-300 rounded px-2 py-1 w-24 text-sm outline-none"
                      >
                        <option value="">--</option>
                        {LAYERING_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                  )}
                  <div className="border border-gray-200 rounded p-2 space-y-2">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1">Skała {s.idx}:</label>
                      <select
                        value={s.rockValue}
                        onChange={(e) => setRockPart(s.idx, e.target.value)}
                        className="border border-gray-300 rounded px-2 py-1.5 w-full text-sm outline-none"
                        disabled={rockDict.length === 0}
                      >
                        <option value="">{s.idx === 1 ? '-- wybierz --' : '-- brak --'}</option>
                        {optionsWithCurrent(rockDict, s.rockValue).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                    {s.rockValue && (
                      <div className="space-y-1.5 pl-2 border-l-2 border-gray-100">
                        <div className="text-xs text-gray-500">Podkategoria w nawiasie (z subCatRockySoil, opcjonalne):</div>
                        {subDict.length === 0 && (
                          <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                            Słownik <code>subCatRockySoil</code> jest pusty w Firestore — dropdowny pokazują tylko „brak". Dodaj wpisy w formacie <code>"SHORT - FULL_NAME"</code> jako doc ID.
                          </div>
                        )}
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Podkat. 1:</label>
                          <select
                            value={s.sub1Value}
                            onChange={(e) => setSubSlot(s.idx, 1, e.target.value)}
                            className="border border-gray-300 rounded px-2 py-1 w-full text-sm outline-none"
                            disabled={subDict.length === 0}
                          >
                            <option value="">-- brak --</option>
                            {optionsWithCurrent(subDict, s.sub1Value).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                        </div>
                        {s.sub1Value && (
                          <div className="grid grid-cols-[70px_1fr] gap-2 items-end">
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Sub-pr. 1:</label>
                              <select
                                value={s.sep1}
                                onChange={(e) => setSubSep(s.idx, 1, e.target.value)}
                                className="border border-gray-300 rounded px-1 py-1 w-full text-sm outline-none"
                              >
                                <option value="">--</option>
                                {LAYERING_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Podkat. 2:</label>
                              <select
                                value={s.sub2Value}
                                onChange={(e) => setSubSlot(s.idx, 2, e.target.value)}
                                className="border border-gray-300 rounded px-2 py-1 w-full text-sm outline-none"
                                disabled={subDict.length === 0}
                              >
                                <option value="">-- brak --</option>
                                {optionsWithCurrent(subDict, s.sub2Value).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                              </select>
                            </div>
                          </div>
                        )}
                        {s.sub2Value && (
                          <div className="grid grid-cols-[70px_1fr] gap-2 items-end">
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Sub-pr. 2:</label>
                              <select
                                value={s.sep2}
                                onChange={(e) => setSubSep(s.idx, 2, e.target.value)}
                                className="border border-gray-300 rounded px-1 py-1 w-full text-sm outline-none"
                              >
                                <option value="">--</option>
                                {LAYERING_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Podkat. 3:</label>
                              <select
                                value={s.sub3Value}
                                onChange={(e) => setSubSlot(s.idx, 3, e.target.value)}
                                className="border border-gray-300 rounded px-2 py-1 w-full text-sm outline-none"
                                disabled={subDict.length === 0}
                              >
                                <option value="">-- brak --</option>
                                {optionsWithCurrent(subDict, s.sub3Value).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                              </select>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </React.Fragment>
              ))}
            </div>
          )}

          {(lithoDict.length === 0 || (isRock && rockDict.length === 0)) && (
            <div className="text-xs text-amber-600 mt-2">
              Słownik niezaładowany (brak kolekcji w Firestore lub jeszcze się ładuje).
            </div>
          )}
        </div>

        {/* Numer warstwy + Geneza */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Numer warstwy:</label>
            <select
              value={dialog.geotechnicalLayer || ''}
              onChange={(e) => update({ geotechnicalLayer: e.target.value })}
              className="border border-gray-300 rounded px-2 py-1.5 w-full text-sm outline-none"
            >
              <option value="">-- brak --</option>
              {numberDict.map(n => <option key={n.id} value={n.id}>{n.id}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Geneza i stratygrafia:</label>
            <select
              value={genesisValue}
              onChange={(e) => {
                // W bazie zapisujemy TYLKO shortForm (np. "Qh"), nie pelny fullString
                const { shortForm } = parseDictEntry(e.target.value);
                update({ genesisAndStratigraphy: shortForm });
              }}
              className="border border-gray-300 rounded px-2 py-1.5 w-full text-sm outline-none"
              disabled={genesisDict.length === 0}
            >
              <option value="">-- brak --</option>
              {optionsWithCurrent(genesisDict, genesisValue).map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <div className="text-xs text-gray-400 mt-1">aktualnie: {dialog.genesisAndStratigraphy || '—'}</div>
          </div>
        </div>

        {/* Stan + Szrafur + Kolor */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Stan gruntu:</label>
            <input
              type="text"
              value={dialog.soilState || ''}
              onChange={(e) => update({ soilState: e.target.value })}
              className="border border-gray-300 rounded px-2 py-1.5 w-full text-sm outline-none"
              placeholder="np. tpl, pl, zwarta"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Szrafur:</label>
            <select
              value={dialog.pattern || 'none'}
              onChange={(e) => update({ pattern: e.target.value })}
              className="border border-gray-300 rounded px-2 py-1.5 w-full text-sm outline-none"
            >
              <option value="none">Brak</option>
              <option value="sand">Piasek (kropki)</option>
              <option value="clay">Glina (linie poziome)</option>
              <option value="gravel">Żwir (duże kropki)</option>
              <option value="rock">Skała (krzyżyk)</option>
            </select>
          </div>
        </div>
        <div className="mb-4">
          <label className="block text-sm font-semibold text-gray-700 mb-1">Kolor:</label>
          <input
            type="color"
            value={dialog.color || '#8B4513'}
            onChange={(e) => update({ color: e.target.value })}
            className="w-full h-10 cursor-pointer border border-gray-300 rounded"
          />
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onSubmit}
            className="flex-1 bg-brand-700 text-white px-4 py-2 rounded font-semibold hover:bg-brand-800 transition"
          >
            ✓ Zapisz
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded font-semibold hover:bg-gray-400 transition"
          >
            ✕ Anuluj
          </button>
        </div>

        <div className="text-xs text-gray-500 mt-3 text-center">
          Esc — anuluj
        </div>
      </div>

      <div
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 999 }}
        onClick={onCancel}
      />
    </>
  );
}
